import { Box, Flex, VStack, useBreakpointValue } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Navigation from './Navigation';
import AppSidebar from './AppSidebar';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const location = useLocation();
  
  // Check if we're on a full-screen page (like ContentDetail)
  const isFullScreenPage = location.pathname.includes('/content/') || 
                          location.pathname.includes('/player/') ||
                          location.pathname.includes('/book/') ||
                          location.pathname.includes('/short/');
  const isHomePage = location.pathname === '/';
  
  const defaultPaddingX = useBreakpointValue({ base: 2, sm: 2, md: 3, lg: 4 });
  const homePaddingX = useBreakpointValue({ base: 0, sm: 0, md: 0, lg: 0 });
  const paddingX = isHomePage ? homePaddingX : defaultPaddingX;
  const paddingY = useBreakpointValue({ base: 3, sm: 4, md: 6 });
  const bottomPadding = useBreakpointValue({ base: "80px", md: "70px", lg: "70px" });
  const headerHeight = useBreakpointValue({ base: "52px", md: "80px" });
  const sidebarWidth = useBreakpointValue({ base: "0px", md: "220px", lg: "260px" });

  // For full-screen pages, don't add extra padding
  if (isFullScreenPage) {
    return (
      <VStack minH="100vh" gap={0} w="100%" maxW="100%" overflowX="hidden" style={{ zIndex: 1 }}>
        <Header />
        <Box flex="1" w="100%" maxW="100%" overflowX="hidden" position="relative" style={{ zIndex: 1 }} pt={headerHeight}>
          {children}
        </Box>
        {isMobile && <Navigation />}
        <Footer />
      </VStack>
    );
  }

  return (
    <VStack minH="100vh" gap={0} w="100%" maxW="100%" overflowX="hidden" style={{ zIndex: 1 }}>
      <Header />
      <Flex
        flex="1"
        w="100%"
        maxW="100%"
        direction={{ base: 'column', lg: 'row' }}
        overflowX="hidden"
        style={{ zIndex: 1 }}
        position="relative"
        pt={headerHeight}
      >
        {!isMobile && (
          <Box
            as="aside"
            w={sidebarWidth}
            flexShrink={0}
            position="fixed"
            left={0}
            top={headerHeight}
            bottom={0}
            zIndex={10}
            pt={paddingY}
            pb={bottomPadding}
          >
            <AppSidebar />
          </Box>
        )}
        <Box
          flex="1"
          px={paddingX}
          py={paddingY}
          pb={bottomPadding}
          w="100%"
          maxW="100%"
          overflowX="hidden"
          position="relative"
          style={{ zIndex: 1 }}
          ml={{ base: 0, md: !isMobile ? sidebarWidth : 0 }}
        >
          {children}
        </Box>
        {isMobile && <Navigation />}
      </Flex>
      <Footer />
    </VStack>
  );
};

export default Layout;
